import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@hooks/useAuth";
import { useParams } from "react-router";
import { formDataToObject, unique } from "@/app/util";
import useApi, { useCallAPI } from "@hooks/useApi";
import toasty from "@/app/util/toasty";
import SearchableSelect from "@components/common/Select/SearchableSelect";
import CollapsibleSection from "@components/common/Section/CollapsibleSection";
import Button from "@components/common/Button";
import Checkbox from "@components/common/Checkbox";
import Page from "@components/layout/Page";
import Header from "@components/layout/Header";
import IconUsers from "@assets/icons/icon-user.svg?react";
import useOrganisations from "@hooks/useOrganisations";
import Spinner from "@/app/components/common/Spinner";
import Error from "@/app/components/Error";
import UserGlobalPermissions from "./UserGlobalPermissions.jsx";
import UserOrganisationPermissions from "./UserOrganisationPermissions.jsx";
import { UserPermissionsProvider } from "./UserPermissionsContext.jsx";
import { Permission } from "@/app/components/Auth/permissions.js";

function UserCheckbox({
	data,
	label,
	name,
	checked,
	onSuccess,
	onError,
	mutate,
	...props
}) {
	const callAPI = useCallAPI();
	const handleChange = async (checked) => {
		mutate({ ...data, [name]: checked }, false);

		try {
			await callAPI("users", {
				method: "PATCH",
				query: { id: `eq.${data.id}` },
				payload: { [name]: checked },
			});
			if (onSuccess) {
				onSuccess(checked);
			} else {
				toasty.success("User details updated");
			}
		} catch (error) {
			if (onError) {
				onError(checked);
			} else {
				toasty.error("Could not update user details", error);
			}
		}
		mutate();
	};
	return (
		<Checkbox
			label={label}
			name={name}
			checked={checked}
			onChange={handleChange}
			controlled
			{...props}
		/>
	);
}

function UserContent({ user, mutate }) {
	const callAPI = useCallAPI();
	const { id } = user || {};
	const { user: authUser, checkPermissions } = useAuth();
	const { uwr } = authUser || {};
	const sealord = checkPermissions("none", Permission.SEALORD, false);
	const [userOrganisations, setUserOrganisations] = useState([]);
	const [selectedNewOrganisation, setSelectedNewOrganisation] =
		useState(null);
	const { organisations = [] } = useOrganisations();

	// Process organisations when user data changes
	useEffect(() => {
		if (!user?.roles) {
			setUserOrganisations([]);
			return;
		}

		const roles = unique(
			user.roles,
			(a, b) => a.organisation_id === b.organisation_id
		).filter((p) => !!p.organisation_id);

		const orgs = roles
			.map((role) => {
				const org =
					role.organisation ||
					organisations.find((o) => o.id === role.organisation_id);
				if (!org) {
					return null;
				}
				const canEdit = checkPermissions(
					org,
					Permission.USERS_MANAGEMENT
				);
				if (!canEdit) {
					return null;
				}
				return {
					...org,
					permissions: user.permissions.filter(
						(p) => p.organisation_id === org.id
					),
					role: user.roles.find((r) => r.organisation_id === org.id),
				};
			})
			.filter(Boolean);

		setUserOrganisations(orgs);
	}, [user, organisations, checkPermissions]);

	const handleUserDetailsSubmit = async (e) => {
		e.preventDefault();
		const form = e.target;
		const payload = formDataToObject(new FormData(form));

		try {
			await callAPI("users", {
				method: "PATCH",
				query: { id: `eq.${id}` },
				payload,
			});
			toasty.success("User details updated");
			mutate();
		} catch (error) {
			toasty.error("Could not update user details", error);
		}
	};

	const handleOrganisationDelete = async (organisationId) => {
		if (
			!confirm(
				"Are you sure you want to delete all permissions for this organisation?"
			)
		) {
			return;
		}

		try {
			const results = await callAPI(
				"rpc/permissions_delete_organisation",
				{
					method: "POST",
					payload: {
						oid: organisationId,
						uid: id,
					},
					one: true,
					schema: "portal",
				}
			);
			if (results?.ok) {
				toasty.success("Permissions deleted");
				mutate();
			}
		} catch (error) {
			toasty.error("Failed to delete permissions", error);
		}
	};
	const currentOrganisation = useMemo(
		() =>
			selectedNewOrganisation
				? organisations.find((o) => o.id === selectedNewOrganisation)
				: null,
		[selectedNewOrganisation, organisations]
	);

	if (!user) {
		return <Error>User not found</Error>;
	}
	// NOTE / TODO: non-uwrs with user management permission can not yet edit user details
	const canEditOrView = uwr; // || checkPermissions(userOrganisations.map(u => u.role.organisation_id), Permission.USERS_MANAGEMENT)

	return (
		<div className="space-y-6">
			<form
				onSubmit={handleUserDetailsSubmit}
				className="flex flex-col gap-4 bg-white shadow-md mb-8 p-8"
			>
				<div className="flex flex-col md:flex-row items-start md:items-center gap-4">
					<div>
						<label
							htmlFor="user-name"
							className="block text-sm font-bold mb-1"
						>
							Name
						</label>
						<input
							id="user-name"
							name="name"
							placeholder="Name"
							defaultValue={user.name}
							disabled={!canEditOrView}
							required
						/>
					</div>
					<div>
						<label
							htmlFor="user-email"
							className="block text-sm font-bold mb-1"
						>
							Email
						</label>
						<input
							id="user-email"
							name="email"
							type="email"
							placeholder="Email"
							defaultValue={user.email}
							disabled={!canEditOrView}
							required
						/>
					</div>
				</div>
				{(sealord || uwr) && (
					<UserCheckbox
						data={user}
						label="Underwriter"
						disabled={!sealord}
						name="uwr"
						checked={user.uwr || false}
						mutate={mutate}
						onSuccess={(checked) => {
							toasty.success(
								`User is now ${
									checked ? "an" : "not an"
								} underwriter`
							);
						}}
						onError={() => {
							toasty.error("Failed to update underwriter status");
						}}
					/>
				)}
				{uwr && (
					<UserCheckbox
						data={user}
						label="Banned"
						name="banned"
						checked={user.banned || false}
						mutate={mutate}
						onSuccess={(checked) => {
							toasty.success(
								`User has been ${
									checked ? "banned" : "unbanned"
								}`
							);
						}}
						onError={(checked) => {
							toasty.error(
								`Failed to ${checked ? "ban" : "unban"} user`
							);
						}}
					/>
				)}

				{canEditOrView && (
					<div>
						<Button type="submit" variant="primary">
							Save user details
						</Button>
					</div>
				)}
			</form>

			<UserPermissionsProvider
				initialPermissionSet={userOrganisations[0]?.role?.tag || ""}
				user={user}
				onMutate={mutate}
			>
				{canEditOrView && (
					<UserGlobalPermissions
						userId={id}
						userPermissions={user.permissions}
					/>
				)}

				{canEditOrView && !user.uwr && userOrganisations.length > 0 && (
					<div className="space-y-4">
						{userOrganisations.map((org) => (
							<UserOrganisationPermissions
								key={org.id}
								userId={id}
								organisationId={org.id}
								organisationName={`${org.name} [${org.paris_id}]`}
								userPermissions={org.permissions}
								permissionSet={org.role?.tag}
								onDelete={() =>
									handleOrganisationDelete(org.id)
								}
								disabled={!uwr}
							/>
						))}
					</div>
				)}

				{uwr && !user.uwr && (
					<CollapsibleSection title="Add Organisation">
						<div className="space-y-4">
							<SearchableSelect
								name="organisation_id"
								label="Select organisation"
								data={organisations}
								value={selectedNewOrganisation}
								onChange={(_, value) =>
									setSelectedNewOrganisation(value)
								}
								map={(org) => ({
									label: `${org.name} [${org.paris_id}]`,
									value: org.id,
								})}
								closeOnScroll={false}
								root={document.querySelector("#main-layout")}
							/>
							{selectedNewOrganisation && (
								<UserOrganisationPermissions
									userId={id}
									organisationId={selectedNewOrganisation}
									organisationName={
										currentOrganisation
											? `${currentOrganisation.name} [${currentOrganisation.paris_id}]`
											: "New Organisation"
									}
									userPermissions={[]}
									permissionSet=""
									onSaveSuccess={() =>
										setSelectedNewOrganisation(null)
									}
									isNewOrganisation
								/>
							)}
						</div>
					</CollapsibleSection>
				)}
			</UserPermissionsProvider>
		</div>
	);
}

function User() {
	const { id } = useParams();

	const {
		data: user,
		error: _error,
		isLoading,
		mutate,
	} = useApi(
		id ? "users" : null,
		{
			select: [
				"*",
				"permissions:permissions(*)",
				"roles:users_organisations(*, organisation:organisations(id,name,paris_id,broker_id))",
			].join(","),
			id: `eq.${id}`,
		},
		{ one: true }
	);

	return (
		<Page
			icon="user"
			title="Users"
			url={"/users"}
			breadcrumbs={[{ label: user ? user.name : "User" }]}
		>
			<Header
				icon={IconUsers}
				title="User Details"
				subtitle={user?.name}
				className={`flex flex-col`}
			>
				<div>
					<Button to="/users">Back</Button>
				</div>
			</Header>
			<main className="px-2 py-4 md:p-8 print:p-0">
				{isLoading ? (
					<Spinner />
				) : (
					<UserContent user={user} mutate={mutate} />
				)}
			</main>
		</Page>
	);
}

export default User;
